import React, { useState, useEffect, useMemo } from 'react';
import { ArrowRight, Save, X, AlertCircle, Package, Calendar, Download, Upload, Plus, Check } from 'lucide-react';
import { toast } from 'react-toastify';
import authUtils from '../utils/authUtils';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// Utility function để generate unique ID
const generateUniqueId = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    const timeBase = Date.now().toString(36).toUpperCase().slice(-4);
    result += timeBase.padStart(4, chars.charAt(Math.floor(Math.random() * chars.length)));
    for (let i = 0; i < 4; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
};

// Parse number safely
const parseNumber = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const num = parseInt(value, 10);
    return isNaN(num) ? 0 : Math.max(0, num);
};

const parseDecimal = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const num = parseFloat(value);
    return isNaN(num) ? 0 : Math.max(0, num);
};

// Format number with thousand separator
const formatNumber = (number) => {
    if (number === null || number === undefined || number === '') return '0';
    const num = parseInt(number, 10);
    if (isNaN(num)) return '0';
    return num.toLocaleString('vi-VN');
};

// Format date to YYYY-MM-DD
const formatDateForInput = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// Helper function để chuyển đổi date format
const convertDateFormat = (dateInput) => {
    if (!dateInput) return null;

    // Nếu là số (Excel serial date)
    if (typeof dateInput === 'number') {
        const excelEpoch = new Date(1899, 11, 30);
        const date = new Date(excelEpoch.getTime() + dateInput * 86400000);
        return formatDateForInput(date);
    }

    // Nếu là string
    if (typeof dateInput === 'string') {
        // Try DD/MM/YYYY
        const ddmmyyyyMatch = dateInput.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (ddmmyyyyMatch) {
            const [, day, month, year] = ddmmyyyyMatch;
            return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }

        // Try YYYY-MM-DD
        const yyyymmddMatch = dateInput.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
        if (yyyymmddMatch) {
            const [, year, month, day] = yyyymmddMatch;
            return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
    }

    return null;
};

const ChuyenBaoBiAnhSangEm = ({ onClose, onSuccess, phanBoData, chuyenBaoBiData }) => {
    const [teamAnhList, setTeamAnhList] = useState([]);
    const [teamEmList, setTeamEmList] = useState([]);
    const [loading, setLoading] = useState(false);

    // State cho chuyển bao bì nhiều ngày
    const [chuyenBaoBiDates, setChuyenBaoBiDates] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [validationResults, setValidationResults] = useState(null);
    const [importPreview, setImportPreview] = useState(null);


    // Tính toán tổng đã phân bổ cho từng tổ bao bì anh
    const toAnhStats = useMemo(() => {
        const stats = {};

        phanBoData
            .filter(pb => pb['TÊN HÀNG'] === 'Bao bì anh')
            .forEach(pb => {
                const to = pb['TỔ'];
                if (!stats[to]) {
                    stats[to] = {
                        totalAllocated: 0,
                        records: []
                    };
                }
                stats[to].totalAllocated += parseNumber(pb['KHỐI LƯỢNG']);
                stats[to].records.push(pb);
            });

        return stats;
    }, [phanBoData]);

    // Tính toán tổng đã chuyển từ tổ anh
    const toAnhTransferred = useMemo(() => {
        const transferred = {};

        chuyenBaoBiData.forEach(cbb => {
            const toAnh = cbb['TỔ CHUYỂN'];
            if (!transferred[toAnh]) {
                transferred[toAnh] = 0;
            }
            transferred[toAnh] += parseNumber(cbb['KHỐI LƯỢNG']);
        });

        return transferred;
    }, [chuyenBaoBiData]);

    // Tính toán bao bì khả dụng cho từng tổ em (phân bổ + nhận chuyển - đã chuyển đi)
    const toEmStats = useMemo(() => {
        const stats = {};

        // Cộng từ phân bổ BBE
        phanBoData
            .filter(pb => pb['TÊN HÀNG'] === 'Bao bì em' && pb.type !== 'chuyen_tru' && pb.type !== 'chuyen_cong')
            .forEach(pb => {
                const to = pb['TỔ'];
                if (!stats[to]) stats[to] = { totalAllocated: 0 };
                stats[to].totalAllocated += parseNumber(pb['KHỐI LƯỢNG']);
            });

        // Cộng từ các lần nhận chuyển (Anh sang Em hoặc Em sang Em nhận về)
        chuyenBaoBiData.forEach(cbb => {
            const nghiepVu = cbb['NGHIỆP VỤ'] || '';
            if (nghiepVu === 'Anh sang Em' || nghiepVu === 'Em sang Em') {
                const toNhan = cbb['TỔ NHẬN'];
                if (!stats[toNhan]) stats[toNhan] = { totalAllocated: 0 };
                stats[toNhan].totalAllocated += parseNumber(cbb['KHỐI LƯỢNG']);
            }
        });

        return stats;
    }, [phanBoData, chuyenBaoBiData]);

    // Tính toán tổng đã chuyển đi từ tổ em
    const toEmTransferred = useMemo(() => {
        const transferred = {};

        chuyenBaoBiData
            .filter(cbb => (cbb['NGHIỆP VỤ'] || '') === 'Em sang Em')
            .forEach(cbb => {
                const toChuyen = cbb['TỔ CHUYỂN'];
                if (!transferred[toChuyen]) transferred[toChuyen] = 0;
                transferred[toChuyen] += parseNumber(cbb['KHỐI LƯỢNG']);
            });

        return transferred;
    }, [chuyenBaoBiData]);


    useEffect(() => {
        fetchTeamData();
    }, []);

    const fetchTeamData = async () => {
        try {
            setLoading(true);
            const teamResponse = await authUtils.apiRequest('TO_PBNS', 'Find', {});

            // Lấy tổ bao bì anh
            const teamAnh = [...new Set(
                teamResponse
                    .filter(t => t['NHÓM'] === 'Bao bì anh')
                    .map(t => t['TỔ'])
            )];

            // Lấy tổ bao bì em
            const teamEm = [...new Set(
                teamResponse
                    .filter(t => t['NHÓM'] === 'Bao bì em')
                    .map(t => t['TỔ'])
            )];

            setTeamAnhList(teamAnh);
            setTeamEmList(teamEm);
        } catch (error) {
            console.error('Error fetching team data:', error);
            toast.error('Lỗi khi tải dữ liệu tổ');
        } finally {
            setLoading(false);
        }
    };

    // Effect để khởi tạo template sau khi teamEmList được load
    useEffect(() => {
        if (teamEmList.length > 0 && Object.keys(toAnhStats).length > 0 && chuyenBaoBiDates.length === 0) {
            initializeChuyenBaoBiDates();
        }
    }, [teamEmList, toAnhStats]);

    // Tạo entry rỗng
    const createEmptyEntry = () => {
        const firstToAnh = Object.keys(toAnhStats)[0] || '';
        const firstToEm = teamEmList[0] || '';

        return {
            nghiepVu: 'Anh sang Em', // Mặc định là Anh sang Em
            toAnh: firstToAnh,
            toEm: firstToEm,
            soLong: 0,
            klTheoLongCD: 900,
            khoiLuong: 0, // Cho nghiệp vụ Anh sang Anh
            klTheoLongEM: 900,
            ghiChu: ''
        };
    };

    // Khi đổi nghiệp vụ, reset tổ chuyển/nhận cho phù hợp
    const handleUpdateNghiepVu = (dateIndex, entryIndex, value) => {
        const firstToAnh = Object.keys(toAnhStats)[0] || '';
        const firstToEm = teamEmList[0] || '';

        setChuyenBaoBiDates(prev =>
            prev.map((dateItem, i) =>
                i === dateIndex
                    ? {
                        ...dateItem,
                        entries: dateItem.entries.map((entry, j) => {
                            if (j !== entryIndex) return entry;
                            return {
                                ...entry,
                                nghiepVu: value,
                                toAnh: value === 'Em sang Em' ? firstToEm : firstToAnh,
                                toEm: firstToEm,
                                soLong: 0,
                                khoiLuong: 0
                            };
                        })
                    }
                    : dateItem
            )
        );
    };

    // Khởi tạo với 1 ngày
    const initializeChuyenBaoBiDates = () => {
        setChuyenBaoBiDates([
            {
                date: formatDateForInput(new Date()),
                entries: [createEmptyEntry()]
            }
        ]);
    };

    // ==================== CHUYỂN BAO BÌ FUNCTIONS ====================

    // Thêm ngày mới
    const handleAddDate = () => {
        setChuyenBaoBiDates(prev => [
            ...prev,
            {
                date: formatDateForInput(new Date()),
                entries: [createEmptyEntry()]
            }
        ]);
    };

    // Thêm hàng mới cho một ngày
    const handleAddRow = (dateIndex) => {
        setChuyenBaoBiDates(prev =>
            prev.map((dateItem, i) =>
                i === dateIndex
                    ? {
                        ...dateItem,
                        entries: [...dateItem.entries, createEmptyEntry()]
                    }
                    : dateItem
            )
        );
    };

    // Xóa hàng
    const handleRemoveRow = (dateIndex, entryIndex) => {
        setChuyenBaoBiDates(prev =>
            prev.map((dateItem, i) =>
                i === dateIndex
                    ? {
                        ...dateItem,
                        entries: dateItem.entries.filter((_, j) => j !== entryIndex)
                    }
                    : dateItem
            )
        );
    };

    // Xóa ngày
    const handleRemoveDate = (index) => {
        setChuyenBaoBiDates(prev => prev.filter((_, i) => i !== index));
    };

    // Cập nhật ngày
    const handleUpdateDate = (index, newDate) => {
        setChuyenBaoBiDates(prev =>
            prev.map((item, i) =>
                i === index ? { ...item, date: newDate } : item
            )
        );
    };

    // Cập nhật giá trị của một entry
    const handleUpdateEntry = (dateIndex, entryIndex, field, value) => {
        setChuyenBaoBiDates(prev =>
            prev.map((dateItem, i) =>
                i === dateIndex
                    ? {
                        ...dateItem,
                        entries: dateItem.entries.map((entry, j) =>
                            j === entryIndex
                                ? { ...entry, [field]: value }
                                : entry
                        )
                    }
                    : dateItem
            )
        );
    };

    // Tải template Excel
    const handleDownloadChuyenBaoBiTemplate = () => {
        try {
            // Tạo template với tất cả cặp tổ có thể
            const templateData = [];
            let stt = 1;

            // Template cho Anh sang Em
            Object.keys(toAnhStats).forEach(toAnh => {
                teamEmList.forEach(toEm => {
                    templateData.push({
                        'STT': stt++,
                        'Ngày (DD/MM/YYYY)': '15/01/2025',
                        'Nghiệp vụ': 'Anh sang Em',
                        'Tổ chuyển (Anh)': toAnh,
                        'Tổ nhận': toEm,
                        'Số lồng': 0,
                        'KL/lồng CD (KG)': 900,
                        'Khối lượng (KG)': '',
                        'KL/lồng EM (KG)': 900,
                        'Ghi chú': ''
                    });
                });
            });

            // Template cho Anh sang Anh
            Object.keys(toAnhStats).forEach(toAnhChuyen => {
                Object.keys(toAnhStats).forEach(toAnhNhan => {
                    if (toAnhChuyen !== toAnhNhan) {
                        templateData.push({
                            'STT': stt++,
                            'Ngày (DD/MM/YYYY)': '15/01/2025',
                            'Nghiệp vụ': 'Anh sang Anh',
                            'Tổ chuyển (Anh)': toAnhChuyen,
                            'Tổ nhận': toAnhNhan,
                            'Số lồng': '',
                            'KL/lồng CD (KG)': '',
                            'Khối lượng (KG)': 0,
                            'KL/lồng EM (KG)': '',
                            'Ghi chú': ''
                        });
                    }
                });
            });

            // Template cho Em sang Em
            teamEmList.forEach(toEmChuyen => {
                teamEmList.forEach(toEmNhan => {
                    if (toEmChuyen !== toEmNhan) {
                        templateData.push({
                            'STT': stt++,
                            'Ngày (DD/MM/YYYY)': '15/01/2025',
                            'Nghiệp vụ': 'Em sang Em',
                            'Tổ chuyển (Anh)': toEmChuyen,
                            'Tổ nhận': toEmNhan,
                            'Số lồng': 0,
                            'KL/lồng CD (KG)': '',
                            'Khối lượng (KG)': '',
                            'KL/lồng EM (KG)': 900,
                            'Ghi chú': ''
                        });
                    }
                });
            });

            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(templateData);

            ws['!cols'] = [
                { wch: 5 },   // STT
                { wch: 20 },  // Ngày
                { wch: 15 },  // Nghiệp vụ
                { wch: 20 },  // Tổ chuyển
                { wch: 20 },  // Tổ nhận
                { wch: 12 },  // Số lồng
                { wch: 15 },  // KL/lồng CD
                { wch: 15 },  // Khối lượng
                { wch: 15 },  // KL/lồng EM
                { wch: 30 }   // Ghi chú
            ];

            XLSX.utils.book_append_sheet(wb, ws, 'Template Chuyen Bao Bi');

            const instructions = [
                ['HƯỚNG DẪN NHẬP DỮ LIỆU CHUYỂN BAO BÌ'],
                [],
                ['1. Cột "Ngày": Nhập theo định dạng DD/MM/YYYY (ví dụ: 15/01/2025)'],
                ['2. Cột "Nghiệp vụ": Chọn "Anh sang Em", "Anh sang Anh" hoặc "Em sang Em"'],
                ['3. Cột "Tổ chuyển" và "Tổ nhận": KHÔNG ĐƯỢC THAY ĐỔI'],
                [''],
                ['NGHIỆP VỤ "ANH SANG EM":'],
                ['- Nhập "Số lồng", "KL/lồng CD", "KL/lồng EM"'],
                ['- Cột "Khối lượng" để trống (tự động tính)'],
                ['- Tổ nhận phải là tổ Bao bì em'],
                [''],
                ['NGHIỆP VỤ "ANH SANG ANH":'],
                ['- Chỉ nhập cột "Khối lượng (KG)"'],
                ['- Các cột "Số lồng", "KL/lồng CD", "KL/lồng EM" để trống'],
                ['- Tổ nhận phải là tổ Bao bì anh'],
                [''],
                ['4. Cột "Ghi chú": Tùy chọn'],
                ['5. Sau khi điền xong, lưu file và nhập vào hệ thống'],
                [],
                ['LƯU Ý:'],
                ['- Nghiệp vụ "Anh sang Em": Dòng nào có Số lồng > 0 sẽ được lưu'],
                ['- Nghiệp vụ "Anh sang Anh": Dòng nào có Khối lượng > 0 sẽ được lưu'],
                ['- Nghiệp vụ "Em sang Em": Dòng nào có Số lồng > 0 sẽ được lưu'],
                ['- Tổ chuyển phải có đủ bao bì để chuyển'],
            ];

            const wsInstructions = XLSX.utils.aoa_to_sheet(instructions);
            XLSX.utils.book_append_sheet(wb, wsInstructions, 'Hướng dẫn');

            const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
            const blob = new Blob([wbout], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });

            saveAs(blob, `Template_Chuyen_Bao_Bi_${new Date().getTime()}.xlsx`);
            toast.success('Tải template thành công!');
        } catch (error) {
            console.error('Error downloading template:', error);
            toast.error('Lỗi khi tải template');
        }
    };

    // Import từ Excel
    const handleImportChuyenBaoBiExcel = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        try {
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data);
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json(worksheet);

            if (jsonData.length === 0) {
                toast.error('File Excel không có dữ liệu');
                return;
            }

            // Validate cặp tổ hợp lệ
            const validToAnh = Object.keys(toAnhStats);
            const validToEm = teamEmList;

            const errors = [];
            const warnings = [];
            const parsedDates = new Map();

            jsonData.forEach((row, index) => {
                const rowIndex = index + 2;
                const rowErrors = [];

                const ngayRaw = row['Ngày (DD/MM/YYYY)'];
                const nghiepVu = row['Nghiệp vụ'] || 'Anh sang Em';
                const toAnh = row['Tổ chuyển (Anh)'];
                const toEm = row['Tổ nhận'];
                const soLong = parseDecimal(row['Số lồng'] || 0);
                const klCD = parseNumber(row['KL/lồng CD (KG)']) || 900;
                const khoiLuong = parseNumber(row['Khối lượng (KG)'] || 0);
                const klEM = parseNumber(row['KL/lồng EM (KG)']) || 900;
                const ghiChu = row['Ghi chú'] || '';

                const isAnhSangEm = nghiepVu === 'Anh sang Em';
                const isAnhSangAnh = nghiepVu === 'Anh sang Anh';
                const isEmSangEm = nghiepVu === 'Em sang Em';

                const dateStr = convertDateFormat(ngayRaw);

                if (!dateStr) {
                    rowErrors.push(`Ngày không hợp lệ (${ngayRaw})`);
                }

                if (!isAnhSangEm && !isAnhSangAnh && !isEmSangEm) {
                    rowErrors.push(`Nghiệp vụ "${nghiepVu}" không hợp lệ (chỉ "Anh sang Em", "Anh sang Anh" hoặc "Em sang Em")`);
                }

                // Validate tổ chuyển dựa trên nghiệp vụ
                if (isEmSangEm) {
                    if (!validToEm.includes(toAnh)) {
                        rowErrors.push(`Tổ chuyển "${toAnh}" không hợp lệ (phải là tổ Em)`);
                    }
                } else {
                    if (!validToAnh.includes(toAnh)) {
                        rowErrors.push(`Tổ chuyển "${toAnh}" không hợp lệ`);
                    }
                }

                // Validate tổ nhận dựa trên nghiệp vụ
                if (isAnhSangEm && !validToEm.includes(toEm)) {
                    rowErrors.push(`Tổ nhận "${toEm}" không hợp lệ (phải là tổ Em)`);
                } else if (isAnhSangAnh && !validToAnh.includes(toEm)) {
                    rowErrors.push(`Tổ nhận "${toEm}" không hợp lệ (phải là tổ Anh)`);
                } else if (isEmSangEm && !validToEm.includes(toEm)) {
                    rowErrors.push(`Tổ nhận "${toEm}" không hợp lệ (phải là tổ Em)`);
                } else if (isEmSangEm && toAnh === toEm) {
                    rowErrors.push('Tổ chuyển và tổ nhận không được trùng nhau');
                }

                // Validate dữ liệu theo nghiệp vụ
                if ((isAnhSangEm || isEmSangEm) && soLong < 0) {
                    rowErrors.push('Số lồng không được âm');
                } else if (isAnhSangAnh && khoiLuong < 0) {
                    rowErrors.push('Khối lượng không được âm');
                }

                const hasValidData = ((isAnhSangEm || isEmSangEm) && soLong > 0) || (isAnhSangAnh && khoiLuong > 0);

                if (rowErrors.length > 0) {
                    errors.push(`Dòng ${rowIndex}: ${rowErrors.join('; ')}`);
                } else if (!hasValidData) {
                    if (isAnhSangAnh) {
                        warnings.push(`Dòng ${rowIndex}: Khối lượng = 0 (sẽ bỏ qua)`);
                    } else {
                        warnings.push(`Dòng ${rowIndex}: Số lồng = 0 (sẽ bỏ qua)`);
                    }
                } else {
                    if (!parsedDates.has(dateStr)) {
                        parsedDates.set(dateStr, []);
                    }

                    const dateEntries = parsedDates.get(dateStr);
                    // Tìm xem đã có cặp tổ này với cùng nghiệp vụ chưa
                    const existingEntry = dateEntries.find(e =>
                        e.toAnh === toAnh && e.toEm === toEm && e.nghiepVu === nghiepVu
                    );

                    if (existingEntry) {
                        if (isAnhSangEm || isEmSangEm) {
                            existingEntry.soLong = parseDecimal(existingEntry.soLong) + soLong;
                        } else {
                            existingEntry.khoiLuong = parseNumber(existingEntry.khoiLuong) + khoiLuong;
                        }
                    } else {
                        dateEntries.push({
                            nghiepVu,
                            toAnh,
                            toEm,
                            soLong: (isAnhSangEm || isEmSangEm) ? soLong : 0,
                            klTheoLongCD: isAnhSangEm ? klCD : 0,
                            khoiLuong: isAnhSangAnh ? khoiLuong : 0,
                            klTheoLongEM: (isAnhSangEm || isEmSangEm) ? klEM : 0,
                            ghiChu
                        });
                    }
                }
            });

            let totalRecords = 0;
            parsedDates.forEach((entries) => {
                entries.forEach((entry) => {
                    const isAnhSangAnh = entry.nghiepVu === 'Anh sang Anh';
                    const hasValidData = (!isAnhSangAnh && parseDecimal(entry.soLong) > 0) ||
                        (isAnhSangAnh && parseNumber(entry.khoiLuong) > 0);
                    if (hasValidData) {
                        totalRecords++;
                    }
                });
            });

            const previewData = Array.from(parsedDates.entries())
                .map(([date, entries]) => ({
                    date,
                    entries: entries.filter(e => {
                        const isAnhSangAnh = e.nghiepVu === 'Anh sang Anh';
                        return (!isAnhSangAnh && parseDecimal(e.soLong) > 0) ||
                            (isAnhSangAnh && parseNumber(e.khoiLuong) > 0);
                    })
                }))
                .filter(item => item.entries.length > 0);

            setValidationResults({
                errors,
                warnings,
                isValid: errors.length === 0 && previewData.length > 0,
                totalDays: previewData.length,
                totalRecords
            });

            setImportPreview(previewData);

            if (errors.length === 0 && previewData.length > 0) {
                toast.success(`Đọc thành công ${previewData.length} ngày, ${totalRecords} bản ghi. Vui lòng kiểm tra và xác nhận!`);
            } else if (errors.length > 0) {
                toast.error(`Có ${errors.length} lỗi. Vui lòng kiểm tra và sửa file Excel!`);
            } else {
                toast.error('Không có dữ liệu hợp lệ để nhập');
            }
        } catch (error) {
            console.error('Error importing Excel:', error);
            toast.error('Lỗi khi đọc file Excel: ' + (error.message || 'Không xác định'));
            setImportPreview(null);
            setValidationResults(null);
        }

        event.target.value = '';
    };

    // Xác nhận import
    const handleConfirmChuyenBaoBiImport = () => {
        if (!importPreview || !validationResults?.isValid) {
            toast.error('Dữ liệu không hợp lệ');
            return;
        }

        setChuyenBaoBiDates(importPreview);
        setImportPreview(null);
        setValidationResults(null);

        toast.success(`Đã nhập ${importPreview.length} ngày, ${validationResults.totalRecords} bản ghi vào form. Nhấn "Lưu" để lưu vào database!`);
    };

    // Hủy import
    const handleCancelChuyenBaoBiImport = () => {
        setImportPreview(null);
        setValidationResults(null);
        toast.info('Đã hủy import');
    };

    // Lưu dữ liệu chuyển bao bì
    const handleSave = async () => {
        if (isSubmitting) return;

        if (chuyenBaoBiDates.length === 0) {
            toast.error('Vui lòng thêm ít nhất một ngày');
            return;
        }

        const invalidDate = chuyenBaoBiDates.find(d => !d.date);
        if (invalidDate) {
            toast.error('Vui lòng chọn ngày cho tất cả các dòng');
            return;
        }

        try {
            setIsSubmitting(true);

            const allChuyenBaoBiRecords = [];
            const allNhapBaoBiCTRecords = [];
            let totalValidEntries = 0;

            chuyenBaoBiDates.forEach(dateItem => {
                // Lọc các entry có dữ liệu hợp lệ dựa trên nghiệp vụ
                const validEntries = dateItem.entries.filter(entry => {
                    const isAnhSangEm = entry.nghiepVu === 'Anh sang Em';
                    const isAnhSangAnh = entry.nghiepVu === 'Anh sang Anh';
                    const isEmSangEm = entry.nghiepVu === 'Em sang Em';

                    if (isAnhSangEm || isEmSangEm) {
                        return parseDecimal(entry.soLong) > 0;
                    } else if (isAnhSangAnh) {
                        return parseNumber(entry.khoiLuong) > 0;
                    }
                    return false;
                });

                validEntries.forEach(entry => {
                    const isAnhSangEm = entry.nghiepVu === 'Anh sang Em';
                    const isAnhSangAnh = entry.nghiepVu === 'Anh sang Anh';
                    const isEmSangEm = entry.nghiepVu === 'Em sang Em';

                    // Tính khối lượng dựa trên nghiệp vụ
                    let khoiLuong;
                    if (isAnhSangEm) {
                        khoiLuong = Math.round(parseDecimal(entry.soLong) * parseNumber(entry.klTheoLongCD));
                    } else if (isEmSangEm) {
                        khoiLuong = Math.round(parseDecimal(entry.soLong) * parseNumber(entry.klTheoLongEM));
                    } else {
                        khoiLuong = parseNumber(entry.khoiLuong);
                    }

                    const soLongQuyDoi = isAnhSangEm && parseNumber(entry.klTheoLongEM) > 0
                        ? parseFloat((khoiLuong / parseNumber(entry.klTheoLongEM)).toFixed(2))
                        : isEmSangEm ? parseDecimal(entry.soLong)
                            : 0;

                    // Kiểm tra tổ chuyển có đủ bao bì không
                    if (isEmSangEm) {
                        const allocated = toEmStats[entry.toAnh]?.totalAllocated || 0;
                        const transferred = toEmTransferred[entry.toAnh] || 0;
                        const remaining = allocated - transferred;
                        if (khoiLuong > remaining) {
                            toast.error(`Tổ Em ${entry.toAnh} không đủ bao bì để chuyển ${formatNumber(khoiLuong)} KG (còn ${formatNumber(remaining)} KG)`);
                            throw new Error('Không đủ bao bì');
                        }
                    } else {
                        const allocated = toAnhStats[entry.toAnh]?.totalAllocated || 0;
                        const transferred = toAnhTransferred[entry.toAnh] || 0;
                        const remaining = allocated - transferred;
                        if (khoiLuong > remaining) {
                            toast.error(`Tổ ${entry.toAnh} không đủ bao bì để chuyển ${formatNumber(khoiLuong)} KG (còn ${formatNumber(remaining)} KG)`);
                            throw new Error('Không đủ bao bì');
                        }
                    }

                    // Tạo IDCBB trước để dùng cho cả CHUYENBAOBI và NHAPBAOBI_CT
                    const generatedIdcbb = generateUniqueId();

                    // Bản ghi CHUYENBAOBI
                    allChuyenBaoBiRecords.push({
                        IDCBB: generatedIdcbb,
                        NGÀY: dateItem.date,
                        'NGHIỆP VỤ': entry.nghiepVu || 'Anh sang Em',
                        'TỔ CHUYỂN': entry.toAnh,
                        'TỔ NHẬN': entry.toEm,
                        'SỐ LỒNG': (isAnhSangEm || isEmSangEm) ? parseDecimal(entry.soLong) : 0,
                        'KHỐI LƯỢNG/LỒNG CD': isAnhSangEm ? parseNumber(entry.klTheoLongCD) : 0,
                        'KHỐI LƯỢNG': khoiLuong,
                        'KHỐI LƯỢNG/LỒNG EM': (isAnhSangEm || isEmSangEm) ? parseNumber(entry.klTheoLongEM) : 0,
                        'SỐ LỒNG QUY ĐỔI': soLongQuyDoi,
                        'GHI CHÚ': (entry.ghiChu || '').trim()
                    });

                    if (isEmSangEm) {
                        // Em sang Em: trừ tổ chuyển, cộng tổ nhận (đều là bao bì em)
                        allNhapBaoBiCTRecords.push({
                            ID_CT: generateUniqueId(),
                            IDPB_REF: '',
                            IDCBB_REF: generatedIdcbb,
                            NGÀY: dateItem.date,
                            'TÊN HÀNG': 'Bao bì em',
                            'NGHIỆP VỤ': 'Chuyển',
                            'TỔ': entry.toAnh,
                            'SỐ LỒNG': parseDecimal(entry.soLong),
                            'KHỐI LƯỢNG/ LỒNG': parseNumber(entry.klTheoLongEM),
                            'KHỐI LƯỢNG (KG)': khoiLuong
                        });
                        allNhapBaoBiCTRecords.push({
                            ID_CT: generateUniqueId(),
                            IDPB_REF: '',
                            IDCBB_REF: generatedIdcbb,
                            NGÀY: dateItem.date,
                            'TÊN HÀNG': 'Bao bì em',
                            'NGHIỆP VỤ': 'Nhập',
                            'TỔ': entry.toEm,
                            'SỐ LỒNG': soLongQuyDoi,
                            'KHỐI LƯỢNG/ LỒNG': parseNumber(entry.klTheoLongEM),
                            'KHỐI LƯỢNG (KG)': khoiLuong
                        });
                    } else {
                        // Bản ghi NHAPBAOBI_CT cho tổ chuyển (Chuyển) — Anh sang Em / Anh sang Anh
                        allNhapBaoBiCTRecords.push({
                            ID_CT: generateUniqueId(),
                            IDPB_REF: '',
                            IDCBB_REF: generatedIdcbb,
                            NGÀY: dateItem.date,
                            'TÊN HÀNG': 'Bao bì anh',
                            'NGHIỆP VỤ': 'Chuyển',
                            'TỔ': entry.toAnh,
                            'SỐ LỒNG': isAnhSangEm ? parseDecimal(entry.soLong) : 0,
                            'KHỐI LƯỢNG/ LỒNG': isAnhSangEm ? parseNumber(entry.klTheoLongCD) : 0,
                            'KHỐI LƯỢNG (KG)': khoiLuong
                        });

                        // Bản ghi NHAPBAOBI_CT cho tổ nhận (Nhập)
                        if (isAnhSangEm) {
                            allNhapBaoBiCTRecords.push({
                                ID_CT: generateUniqueId(),
                                IDPB_REF: '',
                                IDCBB_REF: generatedIdcbb,
                                NGÀY: dateItem.date,
                                'TÊN HÀNG': 'Bao bì em',
                                'NGHIỆP VỤ': 'Nhập',
                                'TỔ': entry.toEm,
                                'SỐ LỒNG': soLongQuyDoi,
                                'KHỐI LƯỢNG/ LỒNG': parseNumber(entry.klTheoLongEM),
                                'KHỐI LƯỢNG (KG)': khoiLuong
                            });
                        } else if (isAnhSangAnh) {
                            allNhapBaoBiCTRecords.push({
                                ID_CT: generateUniqueId(),
                                IDPB_REF: '',
                                IDCBB_REF: generatedIdcbb,
                                NGÀY: dateItem.date,
                                'TÊN HÀNG': 'Bao bì anh',
                                'NGHIỆP VỤ': 'Nhập',
                                'TỔ': entry.toEm,
                                'SỐ LỒNG': 0,
                                'KHỐI LƯỢNG/ LỒNG': 0,
                                'KHỐI LƯỢNG (KG)': khoiLuong
                            });
                        }
                    }
                });

                totalValidEntries += validEntries.length;
            });

            if (allChuyenBaoBiRecords.length === 0) {
                toast.error('Vui lòng nhập số lồng cho ít nhất một dòng');
                return;
            }

            // Lưu vào CHUYENBAOBI
            await authUtils.apiRequest('CHUYENBAOBI', 'Add', { Rows: allChuyenBaoBiRecords });

            // Lưu vào NHAPBAOBI_CT
            await authUtils.apiRequest('NHAPBAOBI_CT', 'Add', { Rows: allNhapBaoBiCTRecords });

            toast.success(`Đã chuyển ${totalValidEntries} dòng bao bì cho ${chuyenBaoBiDates.length} ngày thành công!`);
            onSuccess?.();
            onClose();

        } catch (error) {
            if (error.message !== 'Không đủ bao bì') {
                console.error('Error saving transfer:', error);
                toast.error('Lỗi khi lưu chuyển bao bì: ' + (error.message || 'Không xác định'));
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl z-10">
                    <div className="flex justify-between items-center">
                        <div>
                            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                <ArrowRight className="h-6 w-6 text-orange-600" />
                                Chuyển bao bì
                            </h2>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                <div className="p-6">
                    {loading ? (
                        <div className="flex justify-center items-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
                            <span className="ml-2 text-gray-600">Đang tải dữ liệu...</span>
                        </div>
                    ) : (
                        <>
                            {/* Toolbar: Template + Import Excel + Thêm ngày */}
                            <div className="flex justify-between items-center flex-wrap gap-2 mb-6">
                                <h3 className="text-lg font-semibold text-gray-800">
                                    Danh sách ngày ({chuyenBaoBiDates.length})
                                </h3>
                                <div className="flex gap-2">
                                    {/* Tải template */}
                                    <button
                                        onClick={handleDownloadChuyenBaoBiTemplate}
                                        className="px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2 text-sm font-medium"
                                    >
                                        <Download className="w-4 h-4" />
                                        Tải Template
                                    </button>

                                    {/* Nhập từ Excel */}
                                    <label className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 text-sm font-medium cursor-pointer">
                                        <Upload className="w-4 h-4" />
                                        Nhập Excel
                                        <input
                                            type="file"
                                            accept=".xlsx,.xls"
                                            onChange={handleImportChuyenBaoBiExcel}
                                            className="hidden"
                                        />
                                    </label>

                                    {/* Thêm ngày */}
                                    <button
                                        onClick={handleAddDate}
                                        className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm font-medium"
                                    >
                                        <Plus className="w-4 h-4" />
                                        Thêm ngày
                                    </button>
                                </div>
                            </div>

                            {/* Validation Results */}
                            {validationResults && (
                                <div className="space-y-3 mb-6">
                                    {validationResults.errors.length > 0 && (
                                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                            <div className="flex items-start">
                                                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-2 flex-shrink-0" />
                                                <div className="text-sm text-red-800 flex-1">
                                                    <p className="font-medium mb-2">
                                                        Lỗi ({validationResults.errors.length}):
                                                    </p>
                                                    <ul className="list-disc list-inside space-y-1 max-h-40 overflow-y-auto">
                                                        {validationResults.errors.map((err, i) => (
                                                            <li key={i}>{err}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {validationResults.warnings.length > 0 && (
                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                            <div className="flex items-start">
                                                <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" />
                                                <div className="text-sm text-yellow-800 flex-1">
                                                    <p className="font-medium mb-2">
                                                        Cảnh báo ({validationResults.warnings.length}):
                                                    </p>
                                                    <ul className="list-disc list-inside space-y-1 max-h-40 overflow-y-auto">
                                                        {validationResults.warnings.map((warn, i) => (
                                                            <li key={i}>{warn}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {validationResults.isValid && (
                                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                            <h3 className="font-medium text-green-800 mb-3 flex items-center gap-2">
                                                <Check className="w-5 h-5" />
                                                Dữ liệu hợp lệ - Xem trước
                                            </h3>
                                            <div className="grid grid-cols-2 gap-4 mb-4">
                                                <div className="bg-white p-3 rounded-lg">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <Calendar className="w-4 h-4 text-blue-600" />
                                                        <span className="text-sm text-gray-600">Số ngày</span>
                                                    </div>
                                                    <div className="text-lg font-bold text-blue-600">
                                                        {validationResults.totalDays}
                                                    </div>
                                                </div>
                                                <div className="bg-white p-3 rounded-lg">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <Package className="w-4 h-4 text-indigo-600" />
                                                        <span className="text-sm text-gray-600">Tổng bản ghi</span>
                                                    </div>
                                                    <div className="text-lg font-bold text-indigo-600">
                                                        {validationResults.totalRecords}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Preview data */}
                                            {importPreview && importPreview.length > 0 && (
                                                <div className="border border-green-200 rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                                                    <div className="bg-green-100 px-4 py-2 border-b border-green-200">
                                                        <h4 className="text-sm font-medium text-green-800">
                                                            Chi tiết dữ liệu import ({importPreview.length} ngày)
                                                        </h4>
                                                    </div>
                                                    <div className="p-3 space-y-3">
                                                        {importPreview.map((item, idx) => (
                                                            <div key={idx} className="bg-white border border-gray-200 rounded-lg p-3">
                                                                <div className="flex items-center justify-between mb-2">
                                                                    <span className="font-medium text-gray-800 flex items-center gap-2">
                                                                        <Calendar className="w-4 h-4 text-blue-600" />
                                                                        {item.date}
                                                                    </span>
                                                                    <span className="text-xs text-gray-600">
                                                                        {item.entries.length} bản ghi
                                                                    </span>
                                                                </div>
                                                                <div className="space-y-1">
                                                                    {item.entries.map((entry, entryIdx) => (
                                                                        <div
                                                                            key={entryIdx}
                                                                            className="flex justify-between items-center text-xs py-1 px-2 bg-gray-50 rounded"
                                                                        >
                                                                            <span className="text-gray-700">
                                                                                {entry.toAnh} → {entry.toEm}
                                                                            </span>
                                                                            <span className="font-semibold text-green-700">
                                                                                {entry.soLong} lồng
                                                                            </span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Action buttons */}
                                            <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-green-200">
                                                <button
                                                    onClick={handleCancelChuyenBaoBiImport}
                                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm"
                                                >
                                                    Hủy
                                                </button>
                                                <button
                                                    onClick={handleConfirmChuyenBaoBiImport}
                                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm flex items-center gap-2"
                                                >
                                                    <Check className="w-4 h-4" />
                                                    Xác nhận nhập ({validationResults.totalDays} ngày, {validationResults.totalRecords} bản ghi)
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Danh sách các ngày */}
                            {chuyenBaoBiDates.map((dateItem, dateIndex) => (
                                <div key={dateIndex} className="border-2 border-gray-300 rounded-lg p-4 space-y-3 bg-gray-50 mb-6">
                                    {/* Header của mỗi ngày */}
                                    <div className="flex items-center gap-3">
                                        <div className="flex-1">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Ngày {dateIndex + 1} <span className="text-red-500">*</span>
                                            </label>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                                    <Calendar className="w-4 h-4 text-gray-500" />
                                                </div>
                                                <input
                                                    type="date"
                                                    value={dateItem.date}
                                                    onChange={(e) => handleUpdateDate(dateIndex, e.target.value)}
                                                    className="pl-10 p-2.5 border border-gray-300 rounded-lg w-full focus:ring-orange-500 focus:border-orange-500"
                                                />
                                            </div>
                                        </div>
                                        {chuyenBaoBiDates.length > 1 && (
                                            <button
                                                onClick={() => handleRemoveDate(dateIndex)}
                                                className="mt-6 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2 text-sm"
                                            >
                                                <X className="w-4 h-4" />
                                                Xóa
                                            </button>
                                        )}
                                    </div>

                                    {/* Bảng nhập liệu */}
                                    <div className="border border-gray-200 rounded-lg overflow-x-auto bg-white">
                                        <table className="min-w-full divide-y divide-gray-200 text-xs">
                                            <thead className="bg-orange-50">
                                                <tr>
                                                    <th className="px-3 py-2 text-left text-xs font-semibold text-orange-800 uppercase tracking-wider">
                                                        STT
                                                    </th>
                                                    <th className="px-3 py-2 text-left text-xs font-semibold text-orange-800 uppercase tracking-wider">
                                                        Nghiệp vụ
                                                    </th>
                                                    <th className="px-3 py-2 text-left text-xs font-semibold text-orange-800 uppercase tracking-wider">
                                                        Tổ chuyển
                                                    </th>
                                                    <th className="px-3 py-2 text-left text-xs font-semibold text-orange-800 uppercase tracking-wider">
                                                        Tổ nhận
                                                    </th>
                                                    <th className="px-3 py-2 text-left text-xs font-semibold text-orange-800 uppercase tracking-wider">
                                                        Số lồng
                                                    </th>
                                                    <th className="px-3 py-2 text-left text-xs font-semibold text-orange-800 uppercase tracking-wider">
                                                        KL/lồng CD
                                                    </th>
                                                    <th className="px-3 py-2 text-left text-xs font-semibold text-orange-800 uppercase tracking-wider">
                                                        Khối lượng (KG)
                                                    </th>
                                                    <th className="px-3 py-2 text-left text-xs font-semibold text-orange-800 uppercase tracking-wider">
                                                        KL/lồng EM
                                                    </th>
                                                    <th className="px-3 py-2 text-left text-xs font-semibold text-orange-800 uppercase tracking-wider">
                                                        Số lồng QĐ
                                                    </th>
                                                    <th className="px-3 py-2 text-left text-xs font-semibold text-orange-800 uppercase tracking-wider">
                                                        Ghi chú
                                                    </th>
                                                    <th className="px-3 py-2 text-left text-xs font-semibold text-orange-800 uppercase tracking-wider">
                                                        Xóa
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {dateItem.entries.map((entry, entryIndex) => {
                                                    const isAnhSangEm = entry.nghiepVu === 'Anh sang Em';
                                                    const isAnhSangAnh = entry.nghiepVu === 'Anh sang Anh';
                                                    const isEmSangEm = entry.nghiepVu === 'Em sang Em';

                                                    // Tính khối lượng dựa trên nghiệp vụ
                                                    const khoiLuong = isAnhSangEm
                                                        ? Math.round(parseDecimal(entry.soLong) * parseNumber(entry.klTheoLongCD))
                                                        : isEmSangEm
                                                            ? Math.round(parseDecimal(entry.soLong) * parseNumber(entry.klTheoLongEM))
                                                            : parseNumber(entry.khoiLuong || 0);

                                                    const soLongQuyDoi = isAnhSangEm && parseNumber(entry.klTheoLongEM) > 0
                                                        ? parseFloat((khoiLuong / parseNumber(entry.klTheoLongEM)).toFixed(2))
                                                        : isEmSangEm ? parseDecimal(entry.soLong)
                                                            : 0;

                                                    const allocated = isEmSangEm
                                                        ? (toEmStats[entry.toAnh]?.totalAllocated || 0)
                                                        : (toAnhStats[entry.toAnh]?.totalAllocated || 0);
                                                    const transferred = isEmSangEm
                                                        ? (toEmTransferred[entry.toAnh] || 0)
                                                        : (toAnhTransferred[entry.toAnh] || 0);
                                                    const remaining = allocated - transferred;
                                                    const isOverLimit = khoiLuong > remaining;

                                                    return (
                                                        <tr key={entryIndex} className={`hover:bg-gray-50 ${isOverLimit && khoiLuong > 0 ? 'bg-red-50' : ''}`}>
                                                            <td className="px-3 py-2 text-xs text-gray-700">
                                                                {entryIndex + 1}
                                                            </td>
                                                            {/* Cột Nghiệp vụ */}
                                                            <td className="px-3 py-2">
                                                                <select
                                                                    value={entry.nghiepVu || 'Anh sang Em'}
                                                                    onChange={(e) => handleUpdateNghiepVu(dateIndex, entryIndex, e.target.value)}
                                                                    className="w-32 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-xs font-medium"
                                                                >
                                                                    <option value="Anh sang Em">Anh sang Em</option>
                                                                    <option value="Anh sang Anh">Anh sang Anh</option>
                                                                    <option value="Em sang Em">Em sang Em</option>
                                                                </select>
                                                            </td>
                                                            {/* Tổ chuyển */}
                                                            <td className="px-3 py-2">
                                                                <select
                                                                    value={entry.toAnh}
                                                                    onChange={(e) => handleUpdateEntry(dateIndex, entryIndex, 'toAnh', e.target.value)}
                                                                    className="w-20 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-xs"
                                                                >
                                                                    {isEmSangEm
                                                                        ? teamEmList.map(to => (
                                                                            <option key={to} value={to}>{to}</option>
                                                                        ))
                                                                        : Object.keys(toAnhStats).map(to => (
                                                                            <option key={to} value={to}>{to}</option>
                                                                        ))
                                                                    }
                                                                </select>
                                                                {khoiLuong > 0 && (
                                                                    <div className="text-[10px] text-gray-500 mt-1">
                                                                        Còn: {formatNumber(remaining)} KG
                                                                    </div>
                                                                )}
                                                            </td>
                                                            {/* Tổ nhận */}
                                                            <td className="px-3 py-2">
                                                                <select
                                                                    value={entry.toEm}
                                                                    onChange={(e) => handleUpdateEntry(dateIndex, entryIndex, 'toEm', e.target.value)}
                                                                    className="w-20 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-xs"
                                                                >
                                                                    {isAnhSangAnh
                                                                        ? Object.keys(toAnhStats).map(to => (
                                                                            <option key={to} value={to}>{to}</option>
                                                                        ))
                                                                        : teamEmList.map(to => (
                                                                            <option key={to} value={to}>{to}</option>
                                                                        ))
                                                                    }
                                                                </select>
                                                            </td>
                                                            {/* Số lồng - cho Anh sang Em và Em sang Em */}
                                                            <td className="px-3 py-2">
                                                                {(isAnhSangEm || isEmSangEm) ? (
                                                                    <input
                                                                        type="number"
                                                                        step="0.01"
                                                                        min="0"
                                                                        value={entry.soLong}
                                                                        onChange={(e) => handleUpdateEntry(dateIndex, entryIndex, 'soLong', e.target.value)}
                                                                        className="w-20 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-xs"
                                                                        placeholder="0"
                                                                    />
                                                                ) : (
                                                                    <span className="text-gray-400 italic text-xs">-</span>
                                                                )}
                                                            </td>
                                                            {/* KL/lồng CD - chỉ cho Anh sang Em */}
                                                            <td className="px-3 py-2">
                                                                {isAnhSangEm ? (
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        value={entry.klTheoLongCD}
                                                                        onChange={(e) => handleUpdateEntry(dateIndex, entryIndex, 'klTheoLongCD', e.target.value)}
                                                                        className="w-20 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-xs"
                                                                        placeholder="900"
                                                                    />
                                                                ) : (
                                                                    <span className="text-gray-400 italic text-xs">-</span>
                                                                )}
                                                            </td>
                                                            {/* Khối lượng */}
                                                            <td className="px-3 py-2">
                                                                {isAnhSangAnh ? (
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        value={entry.khoiLuong || 0}
                                                                        onChange={(e) => handleUpdateEntry(dateIndex, entryIndex, 'khoiLuong', e.target.value)}
                                                                        className="w-20 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-xs"
                                                                        placeholder="0"
                                                                    />
                                                                ) : (
                                                                    <span className="text-xs font-semibold text-blue-600">
                                                                        {khoiLuong > 0 ? formatNumber(khoiLuong) : '-'}
                                                                    </span>
                                                                )}
                                                            </td>
                                                            {/* KL/lồng EM - cho Anh sang Em và Em sang Em */}
                                                            <td className="px-3 py-2">
                                                                {(isAnhSangEm || isEmSangEm) ? (
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        value={entry.klTheoLongEM}
                                                                        onChange={(e) => handleUpdateEntry(dateIndex, entryIndex, 'klTheoLongEM', e.target.value)}
                                                                        className="w-20 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-xs"
                                                                        placeholder="900"
                                                                    />
                                                                ) : (
                                                                    <span className="text-gray-400 italic text-xs">-</span>
                                                                )}
                                                            </td>
                                                            {/* Số lồng QĐ - cho Anh sang Em và Em sang Em */}
                                                            <td className="px-3 py-2 text-xs font-semibold text-blue-600">
                                                                {(isAnhSangEm || isEmSangEm) && soLongQuyDoi > 0 ? soLongQuyDoi : '-'}
                                                            </td>
                                                            {/* Ghi chú */}
                                                            <td className="px-3 py-2">
                                                                <input
                                                                    type="text"
                                                                    value={entry.ghiChu}
                                                                    onChange={(e) => handleUpdateEntry(dateIndex, entryIndex, 'ghiChu', e.target.value)}
                                                                    className="w-32 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-xs"
                                                                    placeholder="Ghi chú"
                                                                />
                                                            </td>
                                                            {/* Xóa */}
                                                            <td className="px-3 py-2 text-center">
                                                                <button
                                                                    onClick={() => handleRemoveRow(dateIndex, entryIndex)}
                                                                    className="text-red-600 hover:text-red-800"
                                                                    title="Xóa hàng"
                                                                >
                                                                    <X className="w-4 h-4" />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Nút thêm hàng */}
                                    <div className="flex justify-end">
                                        <button
                                            onClick={() => handleAddRow(dateIndex)}
                                            className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-xs"
                                        >
                                            <Plus className="w-4 h-4" />
                                            Thêm hàng
                                        </button>
                                    </div>

                                    {/* Thống kê cho ngày này */}
                                    {dateItem.entries.length > 0 && (
                                        <div className="bg-white border border-gray-200 rounded p-2 text-xs">
                                            <span className="text-gray-700">
                                                <span className="font-semibold">Số hàng:</span>{' '}
                                                <span className="text-orange-600 font-bold">
                                                    {dateItem.entries.length}
                                                </span>
                                                {' | '}
                                                <span className="font-semibold">Đã nhập:</span>{' '}
                                                <span className="text-green-600 font-bold">
                                                    {dateItem.entries.filter(e =>
                                                        e.nghiepVu === 'Anh sang Anh'
                                                            ? parseNumber(e.khoiLuong) > 0
                                                            : parseDecimal(e.soLong) > 0
                                                    ).length}
                                                </span>
                                            </span>
                                        </div>
                                    )}
                                </div>
                            ))}

                            {/* Thống kê tổng */}
                            {chuyenBaoBiDates.length > 0 && (
                                <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-3 text-sm mb-6">
                                    <p className="text-blue-900 font-semibold">
                                        <span>Tổng số ngày: {chuyenBaoBiDates.length}</span>
                                        {' | '}
                                        <span>Tổng số dòng sẽ lưu: </span>
                                        <span className="text-blue-700 font-bold">
                                            {chuyenBaoBiDates.reduce((total, dateItem) =>
                                                total + dateItem.entries.filter(e =>
                                                    e.nghiepVu === 'Anh sang Anh'
                                                        ? parseNumber(e.khoiLuong) > 0
                                                        : parseDecimal(e.soLong) > 0
                                                ).length, 0
                                            )}
                                        </span>
                                    </p>
                                </div>
                            )}

                        </>
                    )}

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 mt-6">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                            Đóng
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={isSubmitting || loading}
                            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
                        >
                            {isSubmitting || loading ? (
                                <>
                                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                                    Đang lưu...
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4" />
                                    Lưu dữ liệu ({chuyenBaoBiDates.reduce((total, dateItem) =>
                                        total + dateItem.entries.filter(e =>
                                            e.nghiepVu === 'Anh sang Anh'
                                                ? parseNumber(e.khoiLuong) > 0
                                                : parseDecimal(e.soLong) > 0
                                        ).length, 0
                                    )} dòng)
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChuyenBaoBiAnhSangEm;
